import {
  resolveShopifyAdminProductId,
  shopifyAdminIdToGraphqlId,
  shopifyAdminIdToVariantGraphqlId,
} from '@utils/shopifyIds';
import { getStoryblokContentBySlug } from '@utils/storyblok';
import { pageTranslations } from '@utils/translations';
import { isPast } from 'date-fns';
import type { GetServerSidePropsContext } from 'next';
import { useMemo } from 'react';

import { csrClient, ssrClient } from '../../apollo/api/avantArteClient';
import { storefrontClient } from '../../apollo/shopify/storefrontClient';
import MetaTags from '../../components/Content/MetaTags/MetaTags';
import Layout from '../../components/Layout';
import { PRODUCTS_PAGE_ROOT_PATH } from '../../constants';
import { PRODUCT_RELEASE_NAME_DRAW } from '../../constants/product';
import { Checkout } from '../../features/checkout/components';
import {
  buyGate,
  checkoutGate,
  privateVariantGate,
  regionGate,
  requestCountry,
} from '../../features/checkout/utils/checkoutGate';
import { parseFrameVariantIdQuery } from '../../features/checkout/utils/framingQuery';
import {
  parsePrivateRoomQuery,
  privateRoomPath,
  PrivateRoomQuery,
} from '../../features/checkout/utils/privateRoomQuery';
import { hasPurchaseOptions } from '../../features/checkout/utils/purchaseOptions';
import { resolveReleaseType } from '../../features/checkout/utils/releaseType';
import { parseWorksQuery } from '../../features/checkout/utils/worksQuery';
import { AvantArteDrawGateway } from '../../features/draws/gateways/AvantArteDrawGateway';
import { AvailableDraw } from '../../features/draws/types';
import { AvantArtePrivateSalesGateway } from '../../features/private-sales/gateways/AvantArtePrivateSalesGateway';
import { AvantArteProductGateway } from '../../gateways/AvantArteProductGateway';
import { ShopifyProductsGateway } from '../../gateways/ShopifyProductsGateway';
import { toCanonicalUrl } from '../../helpers/links';
import { CheckoutProvider } from '../../providers/CheckoutProvider';
import { DrawProvider } from '../../providers/DrawProvider';
import { PreAuthDrawProvider } from '../../providers/PreAuthDrawProvider';
import {
  ProductProvider,
  ProductSourceBackend,
  ProductSources,
  ProductSourceStoryblok,
} from '../../providers/ProductProvider';
import { GeneratedProductReleaseType } from '../../types/generated';
import { ShopifyProductPurchaseDetails } from '../../types/Purchasing';

const drawGateway = new AvantArteDrawGateway(csrClient);

type CheckoutProductSource = ProductSources & {
  backendProduct: ProductSourceBackend;
};

interface CheckoutPageProps {
  initialFrameVariantId: string | null;
  initialSelectedDraws: string[];
  initialWorkIds: string[];
  linkedDraws: AvailableDraw[];
  linkedSources: CheckoutProductSource[];
  locale: string;
  locales: string[];
  mainDraw: AvailableDraw | null;
  mainSource: CheckoutProductSource;
  privateRoom: PrivateRoomQuery | null;
  slug: string;
}

const Page = ({
  initialFrameVariantId,
  initialSelectedDraws,
  initialWorkIds,
  linkedDraws,
  linkedSources,
  locale,
  locales,
  mainDraw,
  mainSource,
  privateRoom,
  slug,
}: CheckoutPageProps) => {
  const productSources = useMemo(
    () => [mainSource, ...linkedSources],
    [linkedSources, mainSource],
  );

  return (
    <Layout
      canonicalUrl={toCanonicalUrl({
        path: `/checkout/${slug}`,
        locale,
        hostname: String(process.env.NEXT_PUBLIC_HOSTNAME),
      })}
      hideHeader={false}
      hideFooter
      hideMenuMain
      hideMenuMainMobileFooter
      locale={locale}
      locales={locales}
      title={mainSource.backendProduct.title}
    >
      <MetaTags robots="noindex" />
      <ProductProvider products={productSources}>
        <DrawProvider
          drawGateway={drawGateway}
          initialSelectedDraws={initialSelectedDraws}
          linkedDraws={linkedDraws}
          mainDraw={mainDraw ?? undefined}
        >
          <PreAuthDrawProvider>
            <CheckoutProvider
              initialFrameVariantId={initialFrameVariantId}
              initialWorkIds={initialWorkIds}
              privateRoom={privateRoom}
              productShopifyId={mainSource.shopifyId}
              productSlug={slug}
            >
              <Checkout />
            </CheckoutProvider>
          </PreAuthDrawProvider>
        </DrawProvider>
      </ProductProvider>
    </Layout>
  );
};

export const getServerSideProps = async (
  context: GetServerSidePropsContext,
) => {
  const {
    locale = 'en',
    locales = ['en'],
    params,
    preview = false,
    query,
  } = context;
  const slug = typeof params?.slug === 'string' ? params.slug : null;

  if (!slug) return { notFound: true };

  const client = ssrClient(context);
  const productsGateway = new AvantArteProductGateway(client);
  const serverDrawGateway = new AvantArteDrawGateway(client);
  const privateSalesGateway = new AvantArtePrivateSalesGateway(client);

  const [backendProducts, storyblokResponse] = await Promise.all([
    productsGateway.getProducts([], [], [slug]),
    getStoryblokContentBySlug(PRODUCTS_PAGE_ROOT_PATH, slug, locale, preview),
  ]);

  const mainProduct = backendProducts[0] ?? null;
  if (!mainProduct) return { notFound: true };

  const privateRoom = parsePrivateRoomQuery(query);
  const redirectToProduct = {
    redirect: {
      destination: `/${PRODUCTS_PAGE_ROOT_PATH}/${slug}`,
      permanent: false,
    },
  };
  const redirectBack = privateRoom
    ? {
        redirect: {
          destination: privateRoomPath(privateRoom.slug, mainProduct.shopifyId),
          permanent: false,
        },
      }
    : redirectToProduct;

  if (!mainProduct.artists.length) return redirectBack;

  if (privateRoom) {
    const { privateLink } = await privateSalesGateway.getPrivateLink(
      privateRoom.slug,
    );

    if (!privateLink) return redirectToProduct;
    if (privateLink.expiresAt && isPast(new Date(privateLink.expiresAt))) {
      return redirectBack;
    }

    const { isBuy, isDraw } = resolveReleaseType(mainProduct.releaseType);
    if (!isBuy && !isDraw) return redirectBack;
  }

  const { isBuy, isDraw } = resolveReleaseType(
    privateRoom ? GeneratedProductReleaseType.Private : mainProduct.releaseType,
  );
  const draw = isDraw
    ? await serverDrawGateway.getDrawByProductId(mainProduct.shopifyId)
    : undefined;

  if (!privateRoom && !checkoutGate({ draw, product: mainProduct })) {
    return redirectToProduct;
  }

  const mainDraw: AvailableDraw | null = draw
    ? { draw, product: mainProduct }
    : null;
  const linkedDraws: AvailableDraw[] = [];

  if (draw?.multisetID) {
    const multisetDraws = await serverDrawGateway.getDrawsByMultiSet(
      draw.multisetID,
    );

    if (multisetDraws?.length) {
      const linkedProducts = await productsGateway.getProducts(
        [],
        multisetDraws.map((multisetDraw) => multisetDraw.productId),
        [],
      );

      multisetDraws.forEach((multisetDraw) => {
        if (multisetDraw.id === draw.id) return;

        const product = linkedProducts.find(
          (linkedProduct) => linkedProduct.shopifyId === multisetDraw.productId,
        );
        if (product) linkedDraws.push({ draw: multisetDraw, product });
      });
    }
  }

  const shopifyProductsGateway = new ShopifyProductsGateway(storefrontClient);
  const purchaseDetailsFor = (
    product: ProductSourceBackend,
    variantIdentifier?: string,
  ) => {
    return shopifyProductsGateway.getProductPurchaseDetails({
      identifier: { id: shopifyAdminIdToGraphqlId(product.shopifyId) },
      releaseName: isDraw ? PRODUCT_RELEASE_NAME_DRAW : null,
      variantIdentifier,
    });
  };

  const mainPurchaseDetails = await purchaseDetailsFor(
    mainProduct,
    privateRoom
      ? shopifyAdminIdToVariantGraphqlId(privateRoom.variantId)
      : undefined,
  );

  if (
    privateRoom &&
    !privateVariantGate(mainPurchaseDetails, privateRoom.variantId)
  ) {
    return redirectBack;
  }

  if (
    !privateRoom &&
    isBuy &&
    !(mainPurchaseDetails && hasPurchaseOptions(mainPurchaseDetails))
  ) {
    return redirectToProduct;
  }

  // The product page tells a buyer the work isn't sold where they are, so an
  // excluded main work sends them back there instead of an empty checkout.
  const country = requestCountry(context.req);
  if (!regionGate(mainPurchaseDetails, country)) return redirectBack;

  // Set members excluded from the buyer's country stay listed like sold-out
  // tiles but can't be selected, so they never reach the Shopify handoff.
  const withRegionGate = (details: ShopifyProductPurchaseDetails | null) => {
    return details && !regionGate(details, country)
      ? { ...details, available: false }
      : details;
  };

  const collectionSet = mainPurchaseDetails?.collectionSet ?? [];
  const setProductIds: string[] = [];

  if (isBuy) {
    collectionSet.forEach((member) => {
      const id = resolveShopifyAdminProductId(member.shopifyId);
      if (id && id !== mainProduct.shopifyId) setProductIds.push(id);
    });
  }

  const setProducts = setProductIds.length
    ? await productsGateway.getProducts([], setProductIds, [])
    : [];

  const setVariantIdFor = (product: ProductSourceBackend) => {
    return collectionSet.find((member) => {
      return (
        resolveShopifyAdminProductId(member.shopifyId) === product.shopifyId
      );
    })?.shopifyVariantId;
  };

  const linkedDrawDetails = await Promise.all(
    linkedDraws.map((linkedDraw) => {
      return purchaseDetailsFor(
        linkedDraw.product,
        setVariantIdFor(linkedDraw.product),
      );
    }),
  );
  // Draws have no unavailable tile, so a linked draw excluded from the
  // buyer's country is left out of the checkout instead.
  const regionDraws = linkedDraws.filter((_, index) => {
    return regionGate(linkedDrawDetails[index], country);
  });
  const setPurchaseDetails = await Promise.all(
    setProducts.map(async (product) => {
      const details = withRegionGate(
        await purchaseDetailsFor(product, setVariantIdFor(product)),
      );

      // Set members face the main work's gate; those it turns away stay
      // listed like sold-out tiles but can't be selected.
      if (details && !privateRoom && !buyGate(product)) {
        return { ...details, available: false };
      }

      return details;
    }),
  );

  const linkedProducts = [
    ...regionDraws.map((linkedDraw) => linkedDraw.product),
    ...setProducts,
  ];
  const linkedPurchaseDetails = [
    ...linkedDrawDetails.filter((details) => regionGate(details, country)),
    ...setPurchaseDetails,
  ];
  const initialWorkIds = parseWorksQuery(query, {
    mainId: mainProduct.shopifyId,
    workIds: [
      mainProduct.shopifyId,
      ...linkedProducts.map((product) => product.shopifyId),
    ],
  });
  const initialSelectedDraws = (mainDraw ? [mainDraw, ...regionDraws] : [])
    .filter((item) => initialWorkIds.includes(item.draw.productId))
    .map((item) => item.draw.id);

  const storyblokData = storyblokResponse?.data as
    | { story?: { content?: ProductSourceStoryblok } }
    | undefined;
  const storyblokProduct = storyblokData?.story?.content ?? null;

  const mainSource: CheckoutProductSource = {
    shopifyId: mainProduct.shopifyId,
    backendProduct: mainProduct,
    shopifyPurchaseDetails: mainPurchaseDetails ?? null,
    storyblokProduct,
  };

  const linkedSources: CheckoutProductSource[] = linkedProducts.map(
    (product, index) => ({
      shopifyId: product.shopifyId,
      backendProduct: product,
      shopifyPurchaseDetails: linkedPurchaseDetails[index] ?? null,
      storyblokProduct: null,
    }),
  );

  return {
    props: {
      initialFrameVariantId: parseFrameVariantIdQuery(query),
      initialSelectedDraws,
      initialWorkIds,
      linkedDraws: regionDraws,
      linkedSources,
      locale,
      locales,
      mainDraw,
      mainSource,
      privateRoom,
      slug,
      ...(await pageTranslations(locale, [
        'checkout',
        'navigation',
        'common',
        'authentication',
        'product',
        'countries',
      ])),
    },
  };
};

export default Page;
